import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronUp, ChevronDown, X } from "lucide-react";
import common from "../../../../utils/common";

const HIGHLIGHT_CLASS = "chat-search-highlighted";
const CURRENT_CLASS = "chat-search-current";

function scrollMsgIntoView(msgId) {
  if (!msgId) return null;
  const el = document.querySelector(`[data-msg-id="${msgId}"]`);
  if (!el) return null;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  return el;
}

const ChatSearchBar = ({ open, query, setQuery, onClose, messages }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef(null);

  const matches = useMemo(() => {
    const q = common.normalizeText(query || "").trim();
    if (!q || !Array.isArray(messages)) return [];
    return messages
      .filter(
        (m) =>
          !m.isUndo &&
          m.content &&
          common.normalizeText(m.content).includes(q),
      )
      .map((m) => m.msgId || m.cliMsgId || m._id)
      .filter(Boolean);
  }, [query, messages]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [open]);

  // Apply highlight classes
  useEffect(() => {
    if (!open) return;
    document
      .querySelectorAll(`.${HIGHLIGHT_CLASS}`)
      .forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
    document
      .querySelectorAll(`.${CURRENT_CLASS}`)
      .forEach((el) => el.classList.remove(CURRENT_CLASS));
    matches.forEach((id, i) => {
      const el = document.querySelector(`[data-msg-id="${id}"]`);
      if (!el) return;
      el.classList.add(HIGHLIGHT_CLASS);
      if (i === currentIndex) el.classList.add(CURRENT_CLASS);
    });
    return () => {
      document
        .querySelectorAll(`.${HIGHLIGHT_CLASS}`)
        .forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
      document
        .querySelectorAll(`.${CURRENT_CLASS}`)
        .forEach((el) => el.classList.remove(CURRENT_CLASS));
    };
  }, [matches, currentIndex, open]);

  // Scroll to current match
  useEffect(() => {
    if (!open || matches.length === 0) return;
    scrollMsgIntoView(matches[currentIndex]);
  }, [matches, currentIndex, open]);

  const next = () => {
    if (matches.length === 0) return;
    setCurrentIndex((i) => (i + 1) % matches.length);
  };
  const prev = () => {
    if (matches.length === 0) return;
    setCurrentIndex((i) => (i - 1 + matches.length) % matches.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) prev();
      else next();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="sticky top-0 z-20 bg-white border-b shadow-sm px-3 py-2 flex items-center gap-2">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Tìm trong cuộc trò chuyện..."
        className="flex-1 outline-none text-sm bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200 focus:border-blue-400"
      />
      <span className="text-xs text-gray-500 tabular-nums min-w-[50px] text-center">
        {matches.length === 0 && query.trim()
          ? "0/0"
          : matches.length > 0
            ? `${currentIndex + 1}/${matches.length}`
            : ""}
      </span>
      <button
        type="button"
        onClick={prev}
        disabled={matches.length === 0}
        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
        aria-label="Previous match"
      >
        <ChevronUp size={16} />
      </button>
      <button
        type="button"
        onClick={next}
        disabled={matches.length === 0}
        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
        aria-label="Next match"
      >
        <ChevronDown size={16} />
      </button>
      <button
        type="button"
        onClick={onClose}
        className="p-1 rounded hover:bg-gray-100 text-gray-500"
        aria-label="Close"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default ChatSearchBar;
